import type { Component } from 'vue'
import { createRouter, createWebHistory, type RouteLocationRaw } from 'vue-router'
import { createAuthGuard, useAuthStore } from '@repo/ui'
import { pinia } from '@client-streamer/pinia'
import { Role, type UserRole } from '@repo/client'
import { hasCompletedStreamerFoundation } from '@client-streamer/foundation/streamerFoundation'
import { studioTemplatesRedirect } from './studioRedirect'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: (): Component => import('@client-streamer/components/layout/StreamerLayout.vue'),
      meta: { auth: true },
      children: [
        {
          path: '',
          name: 'home',
          redirect: { name: 'source-studio' },
        },
        {
          path: 'live',
          name: 'live-control',
          component: (): Component => import('@client-streamer/source-studio/SourceLiveView.vue'),
          meta: {
            title: 'Live desk',
            description: 'Operate the sources installed in the S3T app and OBS.',
          },
        },
        {
          // S3T's maintainers only: error reports the S3T app sent (opt-in).
          path: 'maintainer/app-errors',
          name: 'maintainer-app-errors',
          component: (): Component => import('@client-streamer/maintainer/AppErrorReportsView.vue'),
          meta: { title: 'App error reports', roles: [Role.ADMIN], foundation: false },
        },
        {
          path: 'chatbot',
          name: 'chatbot',
          component: (): Component => import('@client-streamer/chatbot/ChatbotView.vue'),
          meta: { title: 'Commands' },
        },
        {
          path: 'producer',
          name: 'producer',
          component: (): Component => import('@client-streamer/producer/ProducerView.vue'),
          meta: { title: 'Producer' },
        },
        {
          path: 'community',
          name: 'community',
          component: (): Component => import('@client-streamer/community/CommunityView.vue'),
          meta: { title: 'Community' },
        },
        {
          // The S3T app's own window opens its Drafts window for this link; anywhere else, this page explains.
          path: 'clips/draft/:momentId',
          name: 'clip-draft',
          component: (): Component => import('@client-streamer/community/ClipDraftView.vue'),
          meta: { title: 'Vertical draft' },
        },
        { path: 'account', redirect: '/settings/account' },
        { path: 'settings', redirect: '/settings/computer' },
        {
          path: 'settings/account',
          name: 'account',
          component: (): Component => import('@client-streamer/account/AccountView.vue'),
          meta: { title: 'Account' },
        },
        {
          path: 'settings/computer',
          name: 'settings-computer',
          beforeEnter: (to): RouteLocationRaw | true =>
            typeof to.query.bridgeCode === 'string'
              ? {
                  path: '/onboarding',
                  query: { bridgeCode: to.query.bridgeCode },
                }
              : true,
          component: (): Component => import('@client-streamer/settings/ThisComputerView.vue'),
          meta: {
            title: 'This computer',
            description: 'Everything the S3T app on your streaming computer reports about itself.',
          },
        },
        {
          path: 'speech',
          name: 'speech',
          component: (): Component => import('@client-streamer/speech/SpeechView.vue'),
          meta: { title: 'Voice' },
        },
        {
          path: 'earnings',
          name: 'earnings',
          component: (): Component => import('@client-streamer/earnings/CreatorEarningsView.vue'),
          meta: { title: 'Creator earnings' },
        },
        {
          path: 'viewer-media',
          name: 'viewer-media',
          component: (): Component => import('@client-streamer/viewer-media/ViewerMediaView.vue'),
          meta: { title: 'Viewer media' },
        },
        {
          path: 'onboarding',
          name: 'source-onboarding',
          component: (): Component => import('@client-streamer/source-studio/onboarding/SourceOnboardingView.vue'),
          meta: {
            auth: true,
            title: 'Stream-ready',
            description: 'Create your first source and verify it in OBS.',
          },
        },
        {
          path: 'legacy/connect/obs',
          name: 'legacy-obs-connect',
          redirect: { name: 'obs-connect' },
        },
        {
          path: 'legacy/live',
          name: 'legacy-live-control',
          redirect: { name: 'live-control' },
        },
        ...(['avatars', 'sound'] as const).map((kind) => ({
          path: kind,
          name: 'source-' + kind,
          component: (): Component => import('@client-streamer/source-studio/SourceAssetLibrary.vue'),
          props: { kind },
          beforeEnter: (to: import('vue-router').RouteLocationNormalized): RouteLocationRaw | true =>
            kind === 'sound' && typeof to.query.source === 'string' ? { path: '/studio', query: { ...to.query, panel: 'sound' } } : true,
          meta: { title: kind === 'avatars' ? 'Cast' : 'Sounds' },
        })),
        {
          path: 'behaviors',
          name: 'source-behaviors',
          redirect: (to): RouteLocationRaw => ({
            path: '/studio',
            query: typeof to.query.source === 'string' ? { ...to.query, panel: 'behaviors' } : { tab: 'sources' },
          }),
        },
        {
          path: 'overlay',
          name: 'overlay-customize',
          redirect: { name: 'source-studio' },
        },
        {
          path: 'bundles',
          name: 'bundles',
          component: (): Component => import('@client-streamer/source-studio/bundles/BundlesView.vue'),
          meta: {
            title: 'Bundles',
            description: 'A whole stream in one go: scenes, a stinger and matching behaviour.',
          },
        },
        {
          path: 'schedule',
          name: 'schedule',
          component: (): Component => import('@client-streamer/schedule/ScheduleView.vue'),
          meta: {
            title: 'Schedule',
            description: 'Plan your streams; Twitch and your Discord events follow.',
          },
        },
        {
          path: 'studio',
          name: 'source-studio',
          beforeEnter: studioTemplatesRedirect,
          component: (): Component => import('@client-streamer/source-studio/StudioWorkspace.vue'),
          meta: {
            title: 'Scenes & sources',
            description: 'Design, rehearse, and publish your stream sources.',
          },
        },
        {
          path: 'connections',
          alias: 'connect/obs',
          name: 'obs-connect',
          beforeEnter: (to): RouteLocationRaw | true =>
            typeof to.query.bridgeCode === 'string'
              ? {
                  path: '/onboarding',
                  query: { bridgeCode: to.query.bridgeCode },
                }
              : true,
          component: (): Component => import('@client-streamer/source-studio/SourceConnectionsView.vue'),
          meta: {
            title: 'Connections',
            description: 'Connect your channel, the S3T app and published sources to OBS.',
          },
        },
        {
          path: 'connections/computer',
          redirect: (to): RouteLocationRaw => ({ path: '/settings/computer', query: to.query }),
        },
        {
          path: 'tuber',
          name: 'tuber-list',
          redirect: { name: 'source-avatars' },
        },
        {
          path: 'tuber/:tuberId/assets',
          name: 'tuber-assets',
          redirect: { name: 'source-avatars' },
        },
        {
          path: 'tuber/:tuberId/select',
          name: 'tuber-select',
          redirect: { name: 'source-avatars' },
        },
        {
          path: 'tuber/:tuberId/overlay',
          name: 'tuber-overlay',
          redirect: { name: 'obs-connect' },
        },
        {
          path: 'twitch',
          name: 'twitch',
          component: (): Component => import('@client-streamer/views/commands/CommandCenterView.vue'),
          meta: {
            title: 'Twitch',
            description: 'Connect Twitch and verify chat/event readiness.',
            roles: [Role.USER, Role.ADMIN],
          },
        },
        {
          path: 'commands',
          redirect: { name: 'twitch' },
        },
        {
          path: 'automations',
          name: 'automations',
          redirect: { name: 'source-behaviors' },
        },
        {
          path: 'discord',
          name: 'discord-integration',
          component: (): Component => import('@client-streamer/views/discord/DiscordIntegrationView.vue'),
          meta: {
            title: 'Discord',
            description: 'Connect Discord as a community input layer.',
            roles: [Role.USER, Role.ADMIN],
          },
        },
        {
          path: 'tts',
          name: 'tts-studio',
          redirect: { name: 'source-sound' },
        },
        {
          path: 'config',
          name: 'config-backup',
          redirect: { name: 'source-studio' },
        },
      ],
    },
    {
      path: '/login',
      name: 'login',
      component: (): Component => import('@client-streamer/views/auth/LoginView.vue'),
      meta: {
        title: 'Login',
        description: 'Sign in to access streamer tools.',
        guest: true,
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: (): Component => import('@client-streamer/views/auth/ForgotPasswordView.vue'),
      meta: {
        title: 'Reset your password',
        description: 'Get a link to choose a new S3T password.',
        guest: true,
      },
    },
    {
      // Open to everyone, signed in or not.
      path: '/privacy',
      name: 'privacy',
      component: (): Component => import('@client-streamer/views/legal/PrivacyView.vue'),
      meta: {
        title: 'Privacy notice',
        description: 'What S3T keeps about you and your viewers, who receives it, and your rights.',
      },
    },
    {
      // Open whether or not someone is signed in: the email's link must always work.
      path: '/reset-password',
      name: 'reset-password',
      component: (): Component => import('@client-streamer/views/auth/ResetPasswordView.vue'),
      meta: {
        title: 'Choose a new password',
        description: 'Set a new S3T password from the emailed link.',
      },
    },
    {
      path: '/setup',
      name: 'foundation-setup',
      component: (): Component => import('@client-streamer/views/foundation/StreamerFoundationView.vue'),
      meta: {
        auth: true,
        foundation: false,
        title: 'Preparing your workspace',
        description: 'Join Shadow & Shout and prepare your personal streamer workspace.',
      },
    },
    {
      path: '/voice/remote',
      name: 'voice-remote',
      component: (): Component => import('@client-streamer/chatbot/PhonePushToTalkView.vue'),
      meta: {
        auth: true,
        title: 'Voice remote',
        description: 'Hold to give a voice command from your phone.',
      },
    },
    {
      path: '/tips/receipt/:id',
      name: 'creator-tip-receipt',
      component: (): Component => import('@client-streamer/earnings/CreatorTipReceiptView.vue'),
      meta: { title: 'Tip receipt', foundation: false },
    },
    {
      path: '/tips/:slug',
      name: 'creator-tip',
      component: (): Component => import('@client-streamer/earnings/CreatorTipView.vue'),
      meta: { title: 'Support the stream', foundation: false },
    },
    {
      path: '/sounds/:slug',
      name: 'sound-list',
      component: (): Component => import('@client-streamer/source-studio/SoundListView.vue'),
      meta: { title: 'Sounds', foundation: false },
    },
    {
      path: '/submit-media/:shareId',
      name: 'viewer-media-submit',
      component: (): Component => import('@client-streamer/viewer-media/ViewerMediaSubmitView.vue'),
      meta: { title: 'Submit media', foundation: false },
    },
    {
      path: '/media-output/:shareId',
      name: 'viewer-media-output',
      component: (): Component => import('@client-streamer/viewer-media/ViewerMediaPlayerView.vue'),
      meta: { title: 'Media output', foundation: false },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: (): Component => import('@client-streamer/views/NotFoundView.vue'),
    },
  ],
})

declare global {
  interface Window {
    __PLAYWRIGHT_MOCK_USER__?: { role: string }
  }
}

function getRole(): UserRole | null {
  const authStore = useAuthStore(pinia)
  return (authStore.user?.role as UserRole) ?? null
}

const authGuard = createAuthGuard({
  loginRoute: 'login',
  homeRoute: 'home',
  getRole,
})

router.beforeEach(authGuard)
router.beforeEach((to) => {
  const authStore = useAuthStore(pinia)
  const isE2EMode = import.meta.env.MODE === 'test' && typeof window !== 'undefined' && window.__PLAYWRIGHT_MOCK_USER__ != null
  const requiresFoundation = to.matched.some((record) => record.meta.auth === true && record.meta.foundation !== false)

  if (isE2EMode || !requiresFoundation || !authStore.user || hasCompletedStreamerFoundation(authStore.user.id)) {
    return true
  }

  return {
    name: 'foundation-setup',
    query: {
      redirect: to.fullPath,
    },
  }
})

export default router
